import { Button, Card, Space, Statistic, Table, Tag, Typography, Row, Col, Alert, Select, InputNumber, Switch, message, Radio } from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";

import { Context } from "../../../context";
import { Fetch, Put } from "../../../fetch";

const { Column } = Table;
const { Text } = Typography;

/**
 * Login-door device check (Fix 1) — anti-farm at MiniPix OTP sign-in.
 * Observe-only: every sign-in gets a Play Integrity verdict recorded (never
 * blocked). This tab surfaces those daily counters from setu
 * (GET /v4/quiz-config/login-integrity-stats) so we can watch adoption and the
 * fake-device rate BEFORE turning on enforcement.
 *
 *   Adoption %  = share of sign-ins from the new app build that sends the signal
 *   Genuine %   = of those, share from real, untampered phones
 *   Fake %      = of those, share from emulator / rooted / bot devices  ← fraud
 */
const LoginIntegrityTab = () => {
  const { token, apiUrl } = useContext(Context);

  const [summary, setSummary] = useState({});
  const [days, setDays] = useState([]);
  const [mode, setMode] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [counter, setCounter] = useState(0);
  // Day-range filter for summary + daily breakdown (setu caps days at 30).
  const [rangeDays, setRangeDays] = useState(14);

  // Editable config (login_integrity_config doc — live within ~1 min, no deploy).
  const [minAppVersion, setMinAppVersion] = useState(0);
  const [requireVersion, setRequireVersion] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/login-integrity-stats?days=${rangeDays}`, token, (result) => {
      if (result && result.success) {
        setSummary(result.summary || {});
        setDays(result.days || []);
        setMode(result.mode || null);
        setEnabled(result.enabled);
        setMinAppVersion(result.minAppVersion ?? 0);
        setRequireVersion(!!result.requireVersion);
      }
    });
  }, [apiUrl, token, counter, rangeDays]);

  const saveConfig = () => {
    setSaving(true);
    Put(
      `${apiUrl}/v4/tasks-config/config/login_integrity_config`,
      token,
      {
        data: {
          loginIntegrityMode: mode || "observe",
          loginMinAppVersion: Number(minAppVersion) || 0,
          loginRequireVersion: !!requireVersion,
        },
      },
      (result) => {
        setSaving(false);
        if (result && result.success) {
          message.success("Saved. Live within ~1 minute (no deploy).");
          setCounter((n) => n + 1);
        } else {
          message.error((result && result.message) || "Save failed");
        }
      },
      () => {
        setSaving(false);
        message.error("Save failed");
      }
    );
  };

  const modeColor = mode === "enforce" ? "red" : mode === "observe" ? "blue" : "default";
  const fmtPct = (v) => (v == null ? "—" : `${v}%`);

  // Derive Genuine/Fake over checked tokens from the daily counters instead of
  // trusting the backend summary: older setu builds computed fake_pct from raw
  // not_ok, which also counts no-signal (old app) sign-ins — that's how the
  // summary once showed "313%". checked − ok = real token failures.
  const derived = days.reduce(
    (a, d) => {
      const c = d.counters || {};
      a.checked += c.mode_checked || 0;
      a.ok += c.ok || 0;
      return a;
    },
    { checked: 0, ok: 0 }
  );
  const fakeCount = Math.max(0, derived.checked - derived.ok);
  const genuinePct = derived.checked ? Math.round((1000 * derived.ok) / derived.checked) / 10 : null;
  const fakePct = derived.checked ? Math.round((1000 * fakeCount) / derived.checked) / 10 : null;

  // Flatten each day's Redis counters into table rows.
  const rows = days.map((d) => {
    const c = d.counters || {};
    return {
      key: d.date,
      date: d.date,
      total: c.total || 0,
      token_sent: c.mode_checked || 0,
      no_token: c.mode_missing || 0,
      genuine: c.ok || 0,
      // Raw not_ok includes no-signal sign-ins (mode_missing) — real token
      // failures = signal sent − genuine. Mirrors the setu summary fix.
      fake: Math.max(0, (c.mode_checked || 0) - (c.ok || 0)),
      fail_open: c.mode_fail_open || 0,
    };
  });

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type={enabled ? "info" : "warning"}
        showIcon
        message={
          <span>
            Login device check is{" "}
            <Tag color={enabled ? "green" : "default"}>
              {enabled ? "ENABLED" : "OFF"}
            </Tag>
            mode <Tag color={modeColor}>{(mode || "unknown").toUpperCase()}</Tag>
          </span>
        }
        description={
          mode === "observe"
            ? "Observe-only: verdicts are recorded but no sign-in is blocked. Watch the numbers below as the new app build rolls out; enforcement is a later, deliberate step."
            : mode === "enforce"
            ? "Enforcement ON: sign-ins from new-enough apps with a bad/missing device verdict are blocked."
            : "Feature off — no verdicts are being recorded. Enable in setu config to start collecting."
        }
      />

      <Card title="Configuration" size="small">
        {!enabled && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Play Integrity master switch (PLAY_INTEGRITY_ENABLED) is OFF in setu env — these settings have no effect until it is enabled."
          />
        )}
        <Row gutter={16} align="bottom">
          <Col span={6}>
            <Text strong>Mode</Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={mode || "observe"}
              onChange={setMode}
              options={[
                { value: "off", label: "off — record nothing" },
                { value: "observe", label: "observe — record, never block" },
                { value: "enforce", label: "enforce — block bad/missing" },
              ]}
            />
          </Col>
          <Col span={6}>
            <Text strong>Min app version (versionCode)</Text>
            <InputNumber
              style={{ width: "100%", marginTop: 4 }}
              min={0}
              value={minAppVersion}
              onChange={(v) => setMinAppVersion(v ?? 0)}
            />
            <Text type="secondary">Enforce only builds ≥ this. First integrity build = 328 (v2.0.2).</Text>
          </Col>
          <Col span={7}>
            <Text strong>Require version (phase 2)</Text>
            <div style={{ marginTop: 4 }}>
              <Switch checked={requireVersion} onChange={setRequireVersion} />{" "}
              <Text type="secondary">
                Block header-less callers too (catches the bot). Flip only after force-upgrade + full adoption.
              </Text>
            </div>
          </Col>
          <Col span={5} style={{ textAlign: "right" }}>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveConfig}>
              Save
            </Button>
          </Col>
        </Row>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            Saved to the <code>login_integrity_config</code> doc — live within ~1 minute, no deploy or restart.
          </Text>
        </div>
      </Card>

      <Card
        title={`Last ${rangeDays} days — summary`}
        size="small"
        extra={
          <Space>
            <Radio.Group
              size="small"
              value={rangeDays}
              onChange={(e) => setRangeDays(e.target.value)}
              options={[
                { label: "7d", value: 7 },
                { label: "14d", value: 14 },
                { label: "30d", value: 30 },
              ]}
              optionType="button"
            />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => setCounter((n) => n + 1)}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Row gutter={16}>
          <Col span={4}>
            <Statistic title="Total sign-ins" value={summary.total_signins ?? 0} />
          </Col>
          <Col span={5}>
            <Statistic
              title="Adoption (sent signal)"
              value={fmtPct(summary.adoption_pct)}
            />
            <Text type="secondary">{summary.token_sent ?? 0} of {summary.total_signins ?? 0}</Text>
          </Col>
          <Col span={5}>
            <Statistic
              title="Genuine devices"
              valueStyle={{ color: "#3f8600" }}
              value={fmtPct(genuinePct)}
            />
            <Text type="secondary">{derived.ok} of {derived.checked} checked</Text>
          </Col>
          <Col span={5}>
            <Statistic
              title="Fake / tampered"
              valueStyle={{ color: "#cf1322" }}
              value={fmtPct(fakePct)}
            />
            <Text type="secondary">{fakeCount} of {derived.checked} checked</Text>
          </Col>
          <Col span={5}>
            <Statistic title="No signal (old app)" value={summary.no_token ?? 0} />
          </Col>
        </Row>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            "Genuine" and "Fake" are computed only over sign-ins that sent a
            signal. Until adoption climbs, most sign-ins show "No signal" — that
            is expected while the app update rolls out.
          </Text>
        </div>
      </Card>

      <Card title="Daily breakdown" size="small">
        <Table dataSource={rows} pagination={false} size="small">
          <Column title="Date" dataIndex="date" width={120} />
          <Column title="Total sign-ins" dataIndex="total" width={120} />
          <Column
            title="Signal sent"
            width={110}
            render={(_, r) => <Tag color="blue">{r.token_sent}</Tag>}
          />
          <Column
            title="Genuine"
            width={100}
            render={(_, r) => <Tag color="green">{r.genuine}</Tag>}
          />
          <Column
            title="Fake / tampered"
            width={130}
            render={(_, r) =>
              r.fake ? <Tag color="red">{r.fake}</Tag> : <Text type="secondary">0</Text>
            }
          />
          <Column
            title="No signal (old app)"
            width={150}
            render={(_, r) => <Text type="secondary">{r.no_token}</Text>}
          />
          <Column
            title="Google unreachable"
            width={150}
            render={(_, r) => <Text type="secondary">{r.fail_open}</Text>}
          />
        </Table>
      </Card>
    </Space>
  );
};

export default LoginIntegrityTab;
